import { Box, ScaleFactorProvider, TouchableBox } from "arkh";
import React, {
  PropsWithChildren,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StyleSheet, View } from "react-native";
import {
  graphql,
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
  useSubscription,
} from "react-relay";
import {
  WidgetCalendarOverview,
  WidgetLayoutProvider,
  WidgetNote,
  WidgetRoutinePositivePercentage,
  WidgetTaskCollection,
  WidgetTaskSingle,
  WidgetTeamSchedule,
  WidgetWeather,
} from "widgets";
import { WidgetEmployeeSchedule } from "widgets/src/WidgetEmployeeSchedule/WidgetEmployeeSchedule";

import { UnitSizeProvider, useUnitSize } from "../../OS/UnitSizeProvider";
import { FileFocusScreen } from "./File/FileFocusScreen";

import { PushAppyFocusScreen } from "./PushAppy/PushAppyFocusScreen";
import { TodoFocusScreen } from "./Todo/TodoFocusScreen";
import { SignalFocusScreen } from "./Signal/SignalFocusScreen";
import { FocusSingleWidgetQuery } from "./__generated__/FocusSingleWidgetQuery.graphql";
import {
  FocusSubscription,
  FocusSubscription$data,
} from "./__generated__/FocusSubscription.graphql";
import { WidgetIntegrationTurnpike } from "widgets/src/WidgetIntegrationTurnpike/WidgetIntegrationTurnpike";
import { WidgetCalendar } from "widgets/src/WidgetCalendar/WidgetCalendar";

import { TeamScheduleFocusScreen } from "./TeamSchedule/TeamScheduleFocusScreen";
import { FocusTVAckMutation } from "./__generated__/FocusTVAckMutation.graphql";
import { useFocusEnd } from "./useFocusEndTV";
import { useEvent } from "widgets/src/useEvent";
import { WidgetFile } from "widgets/src/WidgetFile/WidgetFile";
import { ListFocus } from "./List/ListFocus";
import { TableFocus } from "./Table/TableFocus";
import { FocusType } from "./FocusType";
import { WidgetEmployeeLeave } from "widgets/src/WidgetEmployeeLeave/WidgetEmployeeLeave";
import { sizeToWidthAndHeight } from "widgets/src/utils/sizeToWidthAndHeight";
import { EmployeeRoleFocus } from "./EmployeeRole/EmployeeRoleFocus";

const FocusModalWrapper = (
  props: PropsWithChildren<{
    onPress: () => void;
  }>
) => {
  return (
    <Suspense fallback={null}>
      <View
        style={{
          ...StyleSheet.absoluteFillObject,
          justifyContent: "center",
          alignItems: "center",
          transform: [{ rotateZ: "0deg" }],
        }}
      >
        <TouchableBox
          style={[StyleSheet.absoluteFill, { opacity: 0.7 }]}
          color="darkerText"
          onPress={() => {
            props.onPress();
          }}
        />
        {props.children}
      </View>
    </Suspense>
  );
};

const WIDGET_DETAIL_SCALE_FACTOR = 1.5;
function WidgetWrapper(
  props: PropsWithChildren<{
    forceScale?: number;
    w: number;
    h: number;
    noPointerEvents?: boolean;
  }>
) {
  const { w, h } = props;

  const scale = props.forceScale ?? WIDGET_DETAIL_SCALE_FACTOR;
  const unitSize = useUnitSize() * scale;

  return (
    <ScaleFactorProvider value={unitSize / 236}>
      <WidgetLayoutProvider
        value={{ unitSize, width: w * unitSize, height: h * unitSize, h }}
      >
        <UnitSizeProvider value={unitSize}>
          <Box
            style={{
              alignContent: "center",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: props.noPointerEvents ? "none" : undefined,
            }}
          >
            <Box
              shadow="medium"
              style={{
                width: w * unitSize,
                height: h * unitSize,
              }}
              cornerRadius="medium"
            >
              <Box
                style={{
                  flex: 1,
                  overflow: "hidden",
                  width: w * unitSize,
                  height: h * unitSize,
                }}
                cornerRadius="medium"
              >
                <Suspense
                  fallback={<Box color="surface" style={{ flex: 1 }} />}
                >
                  {props.children}
                </Suspense>
              </Box>
            </Box>
          </Box>
        </UnitSizeProvider>
      </WidgetLayoutProvider>
    </ScaleFactorProvider>
  );
}

const FOCUS_QUERY = graphql`
  query FocusSingleWidgetQuery($nodeId: ID!, $first: Int, $after: String) {
    node(id: $nodeId) {
      ... on WidgetFile {
        ...WidgetFile_widgetFile

        __typename
      }
      ... on WidgetEmployeeSchedule {
        ...WidgetEmployeeSchedule_widgetEmployeeSchedule
        __typename
      }
      ... on WidgetEmployeeLeave {
        ...WidgetEmployeeLeave_widgetEmployeeLeave
        __typename
      }
      ... on WidgetEmployeeRoleOverview {
        id
        overviewSize
        teams {
          name
          icon
          color
        }
        ...WidgetEmployeeRoleOverview_widget
        __typename
      }

      ... on WidgetTodoOverview {
        ...TodoFocusScreen_widgetTodoOverview
        __typename
      }

      ... on WidgetSignalOverview {
        ...SignalFocusScreen_widgetSignalOverview
        __typename
      }

      ... on WidgetTeamSchedule {
        ...WidgetTeamSchedule_widget
        ...TeamScheduleFocusScreen_widgetTeamSchedule
        ...useWidgetToPosSingleSize_widget
        __typename
      }
      ... on WidgetWeather {
        ...WidgetWeather_widget
        ...useWidgetToPosSingleSize_widget
        __typename
      }
      ... on WidgetTodoOverview {
        ...WidgetTodoOverview_widget
        ...useWidgetToPosSingleSize_widget
        __typename
      }
      ... on WidgetNote {
        ...WidgetNote_widgetNote
        ...useWidgetToPosSingleSize_widget
        noteSize
        __typename
      }
      ... on WidgetTaskSingle {
        ...WidgetTaskSingle_widgetTaskSingle
        ...useWidgetToPosSingleSize_widget
        __typename
      }
      ... on WidgetTaskCollection {
        ...WidgetTaskCollection_widgetTaskCollection
        ...useWidgetToPosSingleSize_widget
        __typename
      }
      ... on WidgetRoutineOverview {
        ...WidgetRoutineOverview_widgetRoutineOverview
        ...useWidgetToPosSingleSize_widget
        __typename
      }
      ... on WidgetRoutinePositivePercentage {
        ...WidgetRoutinePositivePercentage_widgetRoutinePositivePercentage
        ...useWidgetToPosSingleSize_widget
        __typename
      }

      ... on WidgetIntegrationPushAppy {
        webDashboardLink
        __typename
      }
      ... on WidgetIntegrationTurnpike {
        ...WidgetIntegrationTurnpike_widgetIntegrationTurnpike
        turnpikeSize
        __typename
      }
      ... on WidgetCalendar {
        ...WidgetCalendar_widgetCalendar
        __typename
      }
      ... on WidgetCalendarOverview {
        ...WidgetCalendarOverview_widget
        __typename
      }
      ... on File {
        ...FileFocusScreen_file

        __typename
      }
      ... on WidgetList {
        ...ListFocus_listWidget
        __typename
      }
      ... on WidgetTable {
        ...TableFocus_tableWidget
        __typename
      }
    }
  }
`;

function WidgetFocus(props: {
  query: PreloadedQuery<FocusSingleWidgetQuery>;
  subject: FocusType | null;
  onDismiss: () => void;
  storecastId: string;
  touchMode: boolean;
  onFocusItem: (id: string) => void;
}) {
  const data = usePreloadedQuery<FocusSingleWidgetQuery>(
    FOCUS_QUERY,
    props.query
  );

  const detailId = props.subject?.detailItemId ?? undefined;

  const { onDismiss } = props;

  switch (data.node?.__typename) {
    case "WidgetEmployeeSchedule":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper forceScale={1} w={8} h={4}>
            <WidgetEmployeeSchedule widget={data.node} mode="IDLE" />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    case "WidgetFile":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper w={6} h={4} forceScale={1}>
            <WidgetFile
              idiom={props.touchMode ? "TOUCH_READONLY" : "STATIC"}
              widget={data.node}
              pageId={props.subject?.detailItemId ?? undefined}
              mode="IDLE"
              isFocused={true}
            />
          </WidgetWrapper>
        </FocusModalWrapper>
      );

    case "WidgetNote":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper
            forceScale={data.node.noteSize === "TWO_X_ONE" ? 3 : 2}
            w={2}
            h={data.node.noteSize === "TWO_X_ONE" ? 1 : 2}
          >
            <WidgetNote
              idiom={props.touchMode ? "TOUCH_READONLY" : "STATIC"}
              widget={data.node}
              pageId={detailId}
              mode="IDLE"
            />
          </WidgetWrapper>
        </FocusModalWrapper>
      );

    case "WidgetTodoOverview":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <TodoFocusScreen
            widget={data.node}
            todoId={detailId}
            storecastId={props.storecastId}
            focusItem={props.onFocusItem}
            isTouchMode={props.touchMode}
          />
        </FocusModalWrapper>
      );

    case "WidgetSignalOverview":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <SignalFocusScreen
            widget={data.node}
            signalId={detailId}
            storecastId={props.storecastId}
            focusItem={props.onFocusItem}
            isTouchMode={props.touchMode}
          />
        </FocusModalWrapper>
      );

    case "WidgetTeamSchedule":
      if (data.node) {
        return (
          <FocusModalWrapper onPress={onDismiss}>
            <WidgetWrapper noPointerEvents forceScale={1} w={8} h={4}>
              <TeamScheduleFocusScreen
                widget={data.node}
                enableAssignees={props.touchMode}
              />
            </WidgetWrapper>
          </FocusModalWrapper>
        );
      }

      return data.node ? (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper forceScale={1} w={3} h={4}>
            <WidgetTeamSchedule widget={data.node} mode="IDLE" />
          </WidgetWrapper>
        </FocusModalWrapper>
      ) : null;

    case "WidgetIntegrationPushAppy":
      return data.node ? (
        <FocusModalWrapper onPress={onDismiss}>
          <PushAppyFocusScreen webLink={data.node.webDashboardLink} />
        </FocusModalWrapper>
      ) : null;

    case "WidgetRoutinePositivePercentage":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper h={1} w={2}>
            <WidgetRoutinePositivePercentage widget={data.node} mode="IDLE" />
          </WidgetWrapper>
        </FocusModalWrapper>
      );

    case "WidgetWeather":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper forceScale={2.5} w={2} h={1}>
            <WidgetWeather
              clientForceSize="TWO_X_ONE"
              widget={data.node}
              mode="IDLE"
            />
          </WidgetWrapper>
        </FocusModalWrapper>
      );

    case "WidgetTaskCollection":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper h={1} w={2}>
            <WidgetTaskCollection widget={data.node} mode="IDLE" />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    case "WidgetTaskSingle":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper h={2} w={3}>
            <WidgetTaskSingle
              clientForceSize="TWO_X_ONE"
              maxOccasions={16}
              maxParts={22}
              widget={data.node}
              mode="IDLE"
            />
          </WidgetWrapper>
        </FocusModalWrapper>
      );

    case "WidgetCalendar":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper w={3} h={4} forceScale={1}>
            <WidgetCalendar widget={data.node} mode="IDLE" />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    case "WidgetCalendarOverview":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper w={3} h={4} forceScale={1}>
            <WidgetCalendarOverview
              widget={data.node}
              mode="IDLE"
              idiom={props.touchMode ? "TOUCH_READONLY" : "STATIC"}
              fetchFirst={40}
            />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    case "WidgetIntegrationTurnpike":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper w={2} h={2}>
            <WidgetIntegrationTurnpike
              forceClientSize="TWO_X_TWO"
              widget={data.node}
              mode="IDLE"
            />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    case "File":
      if (!detailId) {
        return null;
      }

      return (
        <FocusModalWrapper onPress={onDismiss}>
          <FileFocusScreen
            file={data.node}
            pageId={detailId}
            scale={props.subject?.scale ?? undefined}
            transformX={props.subject?.transformX ?? undefined}
            transformY={props.subject?.transformY ?? undefined}
          />
        </FocusModalWrapper>
      );

    case "WidgetList":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper h={2} w={2}>
            <ListFocus widget={data.node} />
          </WidgetWrapper>
        </FocusModalWrapper>
      );

    case "WidgetTable":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper h={2} w={2}>
            <TableFocus widget={data.node} detailId={detailId} />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    case "WidgetEmployeeLeave":
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper h={2} w={2}>
            <WidgetEmployeeLeave widget={data.node} />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    case "WidgetEmployeeRoleOverview": {
      const { h } = sizeToWidthAndHeight(data.node.overviewSize);
      return (
        <FocusModalWrapper onPress={onDismiss}>
          <WidgetWrapper
            h={Math.max(2, h)}
            w={2}
            forceScale={h > 3 ? 1 : h === 3 ? 1.25 : undefined}
          >
            <EmployeeRoleFocus
              widgetId={data.node.id}
              widget={data.node}
              teams={data.node.teams}
              detailId={detailId}
              touchMode={props.touchMode}
              storecastId={props.storecastId}
            />
          </WidgetWrapper>
        </FocusModalWrapper>
      );
    }
    default:
      return null;
  }
}

export const StorecastFocus = (props: {
  storecastId: string;
  shouldAnalyze: boolean;
  touchMode: boolean;
  onHasFocusChanged: (hasFocus: boolean) => void;
  manualFocus?: {
    widgetId: string;
    detailItemId: string | undefined;
  };
  clearManualFocus: () => void;
}) => {
  const [query, loadQuery, disposeQuery] =
    useQueryLoader<FocusSingleWidgetQuery>(FOCUS_QUERY);
  const hasFocus = !!query;
  const onHasFocusChanged = props.onHasFocusChanged;
  useEffect(() => {
    onHasFocusChanged(hasFocus);
  }, [hasFocus, onHasFocusChanged]);
  const [subject, setSubject] = useState<null | FocusSubscription$data>(null);

  const sessionId = subject?.storecastFocus?.sessionId;

  const [ack] = useMutation<FocusTVAckMutation>(graphql`
    mutation FocusTVAckMutation(
      $sessionId: ID!
      $type: StorecastAckFocusType!
    ) {
      ackFocus(sessionId: $sessionId, type: $type)
    }
  `);

  useEffect(() => {
    if (sessionId) {
      ack({
        variables: {
          sessionId,
          type: "RENDERED",
        },
      });
    }
  }, [ack, sessionId]);

  const shouldLoad =
    props.manualFocus &&
    props.manualFocus.widgetId !== subject?.storecastFocus?.subject.nodeId
      ? true
      : false;

  useEffect(() => {
    if (props.manualFocus) {
      if (shouldLoad) {
        loadQuery({
          nodeId: props.manualFocus.widgetId,
        });
      }

      setSubject({
        storecastFocus: {
          sessionId: null,
          setAt: null,
          subject: {
            detailItemId: props.manualFocus.detailItemId ?? null,
            nodeId: props.manualFocus.widgetId,
            scale: null,
            transformX: null,
            transformY: null,
          },
        },
      });
    } else {
      disposeQuery();
      setSubject(null);
    }
  }, [props.manualFocus, disposeQuery, loadQuery, shouldLoad]);

  const load = useEvent((data: FocusSubscription$data | null) => {
    if (data?.storecastFocus?.subject.nodeId) {
      if (
        subject?.storecastFocus?.subject.nodeId !==
        data?.storecastFocus?.subject?.nodeId
      ) {
        loadQuery({
          nodeId: data?.storecastFocus?.subject?.nodeId,
        });
      }

      setSubject(data ?? null);
    } else {
      disposeQuery();
      setSubject(null);
    }
  });

  useSubscription<FocusSubscription>(
    useMemo(
      () => ({
        variables: {
          storecastId: props.storecastId,
        },

        onNext: (data) => {
          if (data?.storecastFocus?.sessionId) {
            ack({
              variables: {
                sessionId: data.storecastFocus.sessionId,
                type: "RECEIVED",
              },
            });
          }
          load(data ?? null);
        },
        subscription: graphql`
          subscription FocusSubscription($storecastId: ID!) {
            storecastFocus(storecastId: $storecastId) {
              setAt
              sessionId
              subject {
                detailItemId
                nodeId
                scale
                transformX
                transformY
              }
            }
          }
        `,
      }),
      [ack, props.storecastId, load]
    )
  );

  const endFocus = useFocusEnd(props.storecastId);
  const _clearManualFocus = props.clearManualFocus;
  const onDismiss = useCallback(() => {
    if (props.touchMode) {
      endFocus();
      disposeQuery();
      setSubject(null);
      _clearManualFocus();
    }
  }, [disposeQuery, endFocus, props.touchMode, _clearManualFocus]);

  const onFocusItem = useCallback((id: string) => {
    setSubject((p) => {
      if (p?.storecastFocus?.subject.nodeId) {
        return {
          ...p,
          storecastFocus: {
            ...p.storecastFocus,
            subject: {
              ...p.storecastFocus.subject,
              detailItemId: id,
            },
          },
        };
      }
      return null;
    });
  }, []);
  return (
    <Suspense fallback={null}>
      {query ? (
        <WidgetFocus
          query={query}
          subject={subject?.storecastFocus?.subject ?? null}
          onDismiss={onDismiss}
          storecastId={props.storecastId}
          touchMode={props.touchMode}
          onFocusItem={onFocusItem}
        />
      ) : null}
    </Suspense>
  );
};
